import AddIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { RobotoStyledPopoverMenu } from "@/shared/components/RobotoStyledPopoverMenu";
import { AddToSessionDialog } from "@/shared/components/sessions/AddToSessionDialog";
import {
  FileSystemNode,
  selectedDirectoriesToPatterns,
  selectedItemsToFileIds,
} from "@/shared/domain/files/filesystem";

import styles from "./SessionsSubmenu.module.css";

interface SessionsSubmenuProps {
  datasetId: string;
  /**
   * The file-browser selection. Files prefill a session by id; directories become
   * recursive `dir/**` session patterns.
   */
  selectedItems: FileSystemNode[];
  /**
   * The parent selection menu's Paper element. The fly-out anchors to it so the
   * two menus align edge-to-edge and match height.
   */
  anchorEl: HTMLElement | null;
  open: boolean;
  /** Close just the fly-out. */
  onClose: () => void;
  /** Close the entire selection menu stack (fly-out + parent menu). */
  onCloseMenu: () => void;
}

/**
 * Sessions fly-out for the selection menu: offers "Create new" and "Add to
 * existing" for a dataset-scoped file selection, and owns the
 * add-to-session dialog plus its success feedback.
 */
export function SessionsSubmenu({
  datasetId,
  selectedItems,
  anchorEl,
  open,
  onClose,
  onCloseMenu,
}: SessionsSubmenuProps) {
  const { goto } = useNavigation();
  const showNotification = useNotification();
  const [addToSessionOpen, setAddToSessionOpen] = React.useState(false);

  return (
    <>
      <RobotoStyledPopoverMenu
        id="sessions-submenu"
        MenuListProps={{
          "aria-labelledby": "sessions-submenu-button",
        }}
        slotProps={{
          paper: {
            className: styles.submenuPaper,
            // Only the height is runtime-computed: match the parent menu's
            // Paper so the two panels' top and bottom edges align.
            style: { height: anchorEl?.offsetHeight },
          },
        }}
        anchorEl={anchorEl}
        open={open && anchorEl !== null}
        onClose={onClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem
          disableRipple
          onClick={() => {
            goto.createSession({
              datasetId,
              fileIds: selectedItemsToFileIds(selectedItems),
              directoryPatterns: selectedDirectoriesToPatterns(selectedItems),
            });
          }}
        >
          <AddIcon /> Create new
        </MenuItem>
        <MenuItem
          disableRipple
          onClick={() => {
            setAddToSessionOpen(true);
            onCloseMenu();
          }}
        >
          <PlaylistAddIcon /> Add to existing
        </MenuItem>
      </RobotoStyledPopoverMenu>
      {addToSessionOpen ? (
        <AddToSessionDialog
          open
          datasetId={datasetId}
          selectedItems={selectedItems}
          onClose={() => setAddToSessionOpen(false)}
          onAdded={() => showNotification("Files added to session")}
        />
      ) : null}
    </>
  );
}
